import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { SuccessNotify, FailedNotify } from "../constants/tostify";
import {
  timelineApiSlice,
  useLikeDislikePostMutation,
  useLazyGetPostInteractionsQuery,
  useDeletePostMutation,
  useUpdatePostMutation,
  useLazyGetTimelineCommentsQuery,
  useAddCommentMutation,
  useLazyGetShareTimelineConnectionsQuery,
} from "../features/timeline/timelineApiSlice";
import { useLazyGetShopByIdQuery } from "../features/shop/shopApiSlice";

/**
 * useViewPostState
 *
 * Extracts all state and business logic from Viewpost.js so the component
 * stays a thin rendering layer.
 *
 * @param {object} params
 * @param {string}   params.id              - shop / vendor id from route
 * @param {string}   params.timelineid      - timeline document id
 * @param {string}   params.mysetting       - active tab ("home" | businessType)
 * @param {string}   params.getbusinesstype - business type string
 * @param {string}   params.mybusinessType  - current user business type
 * @param {Function} params.getTimelinePosts - refetch callback from parent
 * @param {Function} params.setGetmyTimeline
 * @param {Function} params.setShareTimeline
 * @param {Array}    params.getmyTimeline
 * @param {Array}    params.shareTimeline
 */
export const useViewPostState = ({
  id,
  timelineid,
  mysetting,
  getbusinesstype,
  mybusinessType,
  getTimelinePosts,
  setGetmyTimeline,
  setShareTimeline,
  getmyTimeline,
  shareTimeline,
  sharedToOtherTimeline,
}) => {
  const { name } = useSelector((state) => state.user);
  const dispatch = useDispatch();

  // ── UI State ──────────────────────────
  const [comment, setComment] = useState("");
  const [modalopen, setModalopen] = useState(0);
  const [shows, setShows] = useState(false);
  const [postId, setPostId] = useState();
  const [postLikes, setPostLikes] = useState({});
  const [postDislikes, setPostDislikes] = useState({});
  const [postStatus, setPostStatus] = useState({});
  const [active, setActive] = useState("");
  const [showshare, setShowshare] = useState();
  const [openCommentPostId, setOpenCommentPostId] = useState(null);
  const [showmodal, setShowmodal] = useState(false);
  const [shareurl, setShareurl] = useState("");
  const [comments, setComments] = useState();
  const [composerIdentity, setComposerIdentity] = useState({
    username: "",
    imageUrl: [],
  });
  const [sharetoOthers, setShareToOthers] = useState([]);
  // Shop the post was originally posted under (for share association)
  const [shareOriginShopId, setShareOriginShopId] = useState(null);
  const [updatepost, setUpdatepost] = useState(false);
  const [dummyImages, setDummyImages] = useState(0);
  const [lgShow, setLgShow] = useState(false);
  const [purchase, setPurchase] = useState(false);
  const [updatedata, setUpdatedata] = useState({
    shopid: id,
    userid: name?._id,
    heading: "",
    content: "",
    img: [""],
    video: [""],
  });

  // ── Derived dummy-image count ─────────
  useEffect(() => {
    const timelineLength =
      mysetting === "home"
        ? getmyTimeline?.length || 0
        : mysetting === getbusinesstype
          ? shareTimeline?.length || 0
          : 0;
    setDummyImages(Math.max(0, 2 - timelineLength));
  }, [getmyTimeline, shareTimeline, mysetting, getbusinesstype]);

  // ── Helpers
  const handleCommentClick = (postId) => {
    setOpenCommentPostId((prevId) => (prevId === postId ? null : postId));
  };

  const handleClosemodal = () => setShowmodal(false);
  const handleclose = () => setOpenCommentPostId(0);
  const handleClose = () => setShows(false);

  const resolvePostOriginShopId = (pid) => {
    if (!pid) return id || null;
    const pidStr = String(pid);
    const pickShop = (val) => {
      if (val == null || val === "" || val === "null" || val === "undefined") {
        return null;
      }
      return val?._id || val;
    };

    const own = (getmyTimeline || []).find(
      (p) => String(p?._id || "") === pidStr,
    );
    const fromOwn = pickShop(own?.shopid);
    if (fromOwn) return fromOwn;

    const sharedPools = [
      ...(shareTimeline || []),
      ...(sharedToOtherTimeline || []),
    ];
    const sharedEntry = sharedPools.find((entry) => {
      const entryPid = entry?.postid?._id || entry?.postid;
      return String(entryPid || "") === pidStr;
    });
    const fromSharedPost = pickShop(sharedEntry?.postid?.shopid);
    if (fromSharedPost) return fromSharedPost;
    const fromShareBy = pickShop(sharedEntry?.sharebyshopid);
    if (fromShareBy) return fromShareBy;

    return id || null;
  };

  const handleShowshare = (pid) => {
    if (pid) {
      setPostId(pid);
      setShareOriginShopId(resolvePostOriginShopId(pid));
    }
    setShowshare(true);
    setShowmodal(false);
  };

  const handleShowShareModal = (pid) => {
    if (pid) {
      setPostId(pid);
      setShareOriginShopId(resolvePostOriginShopId(pid));
      setShareurl(`/post/${pid}`);
    }
    setShowshare(false);
    setShowmodal(true);
  };

  const handleCloseshare = () => {
    setShowshare(false);
    setShowmodal(true);
  };

  // ── Timeline like / dislike helpers
  const updateLikeDislikeInTimelines = (postId, likeData, dislikeData) => {
    // Update My Timeline
    setGetmyTimeline((prev) =>
      prev.map((post) =>
        post._id === postId
          ? { ...post, like: likeData, dislike: dislikeData }
          : post,
      ),
    );

    // Update Share Timeline
    setShareTimeline((prev) =>
      prev.map((item) =>
        item.postid?._id === postId
          ? {
              ...item,
              postid: {
                ...item.postid,
                like: likeData,
                dislike: dislikeData,
              },
            }
          : item,
      ),
    );
  };

  const handleClick = (reactionType, postId) => {
    const newState = {
      postid: postId,
      userid: name._id,
      shopid: id,
      businessType: mybusinessType,
      likedislike: reactionType,
    };
    likeSystem(newState, postId);
  };

  const [likeDislikePost] = useLikeDislikePostMutation();
  const [getPostInteractions] = useLazyGetPostInteractionsQuery();
  const [deletePostMutation] = useDeletePostMutation();
  const [updatePostMutation] = useUpdatePostMutation();
  const [getComments] = useLazyGetTimelineCommentsQuery();
  const [addCommentMutation] = useAddCommentMutation();
  const [getShopById] = useLazyGetShopByIdQuery();
  const [getShareTimelineConnections] =
    useLazyGetShareTimelineConnectionsQuery();

  const likeSystem = async (newState, postId) => {
    try {
      const res = await likeDislikePost(newState).unwrap();
      if (res?.success) {
        const response = await getPostInteractions({
          postid: postId,
          userid: name._id,
          shopid: id ? id : "",
        }).unwrap();

        const item = response?.data;
        if (item) {
          updateLikeDislikeInTimelines(postId, item.like, item.dislike);
          setPostLikes((prev) => ({ ...prev, [postId]: item.like.count }));
          setPostDislikes((prev) => ({
            ...prev,
            [postId]: item.dislike.count,
          }));
          setPostStatus((prev) => ({
            ...prev,
            [postId]: item.like.isILike
              ? "like"
              : item.dislike.isIDislike
                ? "dislike"
                : null,
          }));

          // Direct RTK Query Cache Update for real-time UI synchronization
          const queryArgsList = [
            {
              userid: name?._id,
              shopid: id || null,
              businessType: mybusinessType,
            },
            {
              userid: name?._id,
              shopid: id || null,
              businessType: "generalPublic",
            },
          ];

          queryArgsList.forEach((queryArgs) => {
            dispatch(
              timelineApiSlice.util.updateQueryData(
                "getTimelinePosts",
                queryArgs,
                (draft) => {
                  if (!draft || !draft.data) return;
                  draft.data.forEach((tl) => {
                    if (Array.isArray(tl.my)) {
                      tl.my = tl.my.map((p) => {
                        if (p._id === postId) {
                          return {
                            ...p,
                            like: item.like,
                            dislike: item.dislike,
                          };
                        }
                        return p;
                      });
                    }
                    if (Array.isArray(tl.sharebyother)) {
                      tl.sharebyother = tl.sharebyother.map((s) => {
                        if (s.postid?._id === postId) {
                          return {
                            ...s,
                            postid: {
                              ...s.postid,
                              like: item.like,
                              dislike: item.dislike,
                            },
                          };
                        }
                        return s;
                      });
                    }
                    if (Array.isArray(tl.sharedtoother)) {
                      tl.sharedtoother = tl.sharedtoother.map((s) => {
                        if (s.postid?._id === postId) {
                          return {
                            ...s,
                            postid: {
                              ...s.postid,
                              like: item.like,
                              dislike: item.dislike,
                            },
                          };
                        }
                        return s;
                      });
                    }
                  });
                },
              ),
            );
          });
        }
      }
    } catch (error) {
      console.error("Error updating like/dislike:", error);
    }
  };

  // ── Post delete
  const Deleteitem = async (postId) => {
    try {
      await deletePostMutation({ postId, timelineId: timelineid }).unwrap();
      setGetmyTimeline((prevTimeline) =>
        prevTimeline.filter((post) => post._id !== postId),
      );
      setShows(false);
      SuccessNotify("Post Deleted Successfully");
      getTimelinePosts();
    } catch (error) {
      FailedNotify("Failed to Delete. Try Again!");
    }
  };

  // ── Comments ──
  const handleCommentChange = (e) => setComment(e.target.value);

  const toImageList = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.flat(Infinity).filter(Boolean);
    return [raw].filter(Boolean);
  };

  const getTimelineComments = async (postId) => {
    try {
      const response = await getComments(postId).unwrap();
      setComments(response);
    } catch (error) {
      console.log("error fetching comments", error);
      if (error?.status === 404) {
        setComments({ success: true, data: { suggestions: [] } });
      }
    }
  };

  const resolveCommentIdentity = async () => {
    const shopid =
      id && id !== "null" && id !== "undefined" && id !== ""
        ? String(id)
        : null;
    const bt = String(mybusinessType || mysetting || "").trim();
    let username = name?.fullname || name?.username || "User";
    let imageUrl = toImageList(name?.imageUrl);
    let resolvedShopId = null;

    // Shop / company / service profile pages pass a shop ObjectId as `id`.
    // Use that profile's name + logo for comments (not the logged-in user name),
    // regardless of which timeline tab is active.
    if (shopid && /^[a-fA-F0-9]{24}$/.test(shopid)) {
      try {
        const shopRes = await getShopById(shopid).unwrap();
        const shop =
          shopRes?.data?.Shop ||
          shopRes?.data?.shop ||
          shopRes?.data ||
          shopRes?.Shop ||
          shopRes?.shop ||
          shopRes;
        const shopName =
          shop?.shopname ||
          shop?.companyName ||
          shop?.servicename ||
          shop?.name ||
          "";
        const shopImgs = toImageList(
          shop?.image || shop?.imageUrl || shop?.logo || shop?.profileImage,
        );
        // Only treat as shop context when the lookup actually returned a shop/company
        // (getShopById can also return a User doc — skip that).
        const looksLikeShop =
          Boolean(shopName) ||
          Boolean(shop?.businessType) ||
          Array.isArray(shop?.image) ||
          shop?.shopid;
        if (looksLikeShop && shop?._id) {
          resolvedShopId = String(shop._id);
          if (shopName) username = shopName;
          if (shopImgs.length) imageUrl = shopImgs;
        }
      } catch (shopErr) {
        console.warn("Could not resolve shop for comment identity:", shopErr);
      }
    }

    return {
      username,
      imageUrl,
      shopid: resolvedShopId,
      businessType: resolvedShopId
        ? bt && bt !== "home"
          ? bt
          : "retailerShop"
        : bt || "generalPublic",
    };
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const identity = await resolveCommentIdentity();
      if (!cancelled) {
        setComposerIdentity({
          username: identity.username,
          imageUrl: identity.imageUrl,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    name?._id,
    name?.fullname,
    name?.imageUrl,
    mybusinessType,
    mysetting,
  ]);

  const handleSubmit = async () => {
    if (!comment.trim()) {
      FailedNotify("Comment cannot be empty");
      return;
    }

    try {
      const identity = await resolveCommentIdentity();

      const body = {
        postid: postId,
        vendorid: identity.shopid,
        suggestions: [
          {
            userId: name?._id,
            shopid: identity.shopid,
            businessType: identity.businessType,
            username: identity.username,
            userEmail: name?.email || "",
            role: name?.bio?.text || "",
            imageUrl: identity.imageUrl,
            message: comment,
            replies: [],
          },
        ],
      };
      const response = await addCommentMutation(body).unwrap();
      if (response?.success) {
        SuccessNotify("Comment added successfully");
        setComment("");
        // Keep modal open and refresh the full thread so comments do not
        // appear to disappear after several are added.
        if (response?.data?.suggestions) {
          setComments({
            success: true,
            data: response.data,
            message: response.message,
          });
        }
        if (postId) {
          await getTimelineComments(postId);
        }
        if (typeof getTimelinePosts === "function") {
          getTimelinePosts();
        }
      }
    } catch (error) {
      console.error("Add comment failed:", error);
      const errorMessage =
        error?.data?.message ||
        error?.message ||
        "Failed to add comment. Try again!";
      FailedNotify(errorMessage);
    }
  };

  // ── Share ─
  const getShareTimeline = async () => {
    try {
      const response = await getShareTimelineConnections({
        ...(id ? { shopid: id } : {}),
        userid: name._id,
      }).unwrap();
      const list = response?.shareto || [];
      setShareToOthers(Array.isArray(list) ? list : []);
    } catch (error) {
      FailedNotify("Failed to share post. Try again!");
    }
  };

  // ── Post update ─────
  const UpdatePost = async () => {
    try {
      await updatePostMutation({
        timelineId: timelineid,
        postId: postId,
        body: updatedata,
      }).unwrap();
      SuccessNotify("Post Updated Successfully");
      setUpdatepost(false);
      getTimelinePosts();
    } catch (error) {
      FailedNotify("Failed to update post");
    }
  };

  return {
    // state
    name,
    comment,
    modalopen,
    setModalopen,
    shows,
    setShows,
    postId,
    setPostId,
    postLikes,
    postDislikes,
    postStatus,
    active,
    setActive,
    showshare,
    openCommentPostId,
    setOpenCommentPostId,
    showmodal,
    comments,
    composerIdentity,
    resolveCommentIdentity,
    sharetoOthers,
    shareOriginShopId,
    shareurl,
    updatepost,
    setUpdatepost,
    dummyImages,
    lgShow,
    setLgShow,
    purchase,
    setPurchase,
    updatedata,
    setUpdatedata,
    // handlers
    handleCommentClick,
    handleClosemodal,
    handleclose,
    handleClose,
    handleShowshare,
    handleShowShareModal,
    handleCloseshare,
    handleClick,
    Deleteitem,
    handleCommentChange,
    getTimelineComments,
    handleSubmit,
    getShareTimeline,
    UpdatePost,
  };
};
